import { useNavigate } from 'react-router-dom';
import type { CartLine, Currency } from '@/api/models';
import { useUI } from '@/context/UIContext';
import { useCart } from '@/hooks/useCart';
import { useSettings } from '@/hooks/queries';
import { useLineActions } from '@/hooks/useLineActions';
import { formatMoney } from '@/lib/money';
import { Drawer } from './Drawer';
import { LineThumb } from '@/components/cart/LineThumb';

function DrawerLine({ line, currency }: { line: CartLine; currency: Currency }) {
  const a = useLineActions(line);
  return (
    <div className={'line' + (a.busy ? ' busy' : '')}>
      <div className="th"><LineThumb line={line} /></div>
      <div>
        <div className="ln">{line.name}</div>
        <div className="lm">Size {line.size}, {formatMoney(line.unitPrice, currency)}</div>
        <div className="lq">
          <button onClick={a.decrease} aria-label={`Decrease quantity of ${line.name}`}>−</button>
          <span aria-label="Quantity">{line.quantity}</span>
          <button onClick={a.increase} disabled={!a.canIncrease} aria-label={`Increase quantity of ${line.name}`}>+</button>
        </div>
      </div>
      <button className="rm" onClick={a.remove}>Remove</button>
    </div>
  );
}

export function CartDrawer() {
  const { drawer, closeDrawer } = useUI();
  const { data: cart } = useCart();
  const { data: settings } = useSettings();
  const navigate = useNavigate();
  const go = (path: string) => { closeDrawer(); navigate(path); };

  const lines = cart?.lines ?? [];
  const after = cart ? cart.subtotal - cart.discount : 0;
  const threshold = settings?.freeShippingThreshold ?? null;
  const currency = cart?.currency;

  let note = '';
  if (lines.length && threshold !== null) {
    note = after >= threshold ? 'Your order ships free.' : `Add ${formatMoney(threshold - after, currency)} more for free shipping.`;
  }

  return (
    <Drawer
      side="cart"
      title="Your bag"
      open={drawer === 'cart'}
      onClose={closeDrawer}
      footer={
        <>
          <div className="subtotal"><span>Subtotal</span><span>{formatMoney(after, currency)}</span></div>
          {note && <div className="compare" style={{ margin: '-4px 0 12px' }}>{note}</div>}
          <div className="dbtns">
            <button className="ghost" onClick={() => go('/cart')}>View bag</button>
            <button className="checkout" disabled={!lines.length} onClick={() => go('/checkout')}>Check out</button>
          </div>
        </>
      }
    >
      {!lines.length ? (
        <div className="empty">Your bag is empty.<br /><button onClick={() => go('/')}>Browse the rail</button></div>
      ) : (
        lines.map((l) => <DrawerLine key={l.key} line={l} currency={cart!.currency} />)
      )}
    </Drawer>
  );
}
